/**
 * OrgChartControl
 *
 * Visual card-based org chart renderer with collapsible subtrees and
 * node-level action buttons.
 *
 * - Pure React, no external dependencies
 * - Inline styles only
 * - Supports vertical (top-down) and horizontal (left-right) layouts
 * - Card style variants: detailed | compact | minimal
 * - Collapsible subtrees tracked by node-id Set
 * - NodeActions: url (template), form (CustomEvent), event (CustomEvent)
 * - Connector lines drawn with inline divs (no SVG required)
 */

import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ControlRendererProps } from '../ControlRenderer';

// ---------------------------------------------------------------------------
// Config types
// ---------------------------------------------------------------------------

interface NodeAction {
  id: string;
  label: string;
  actionType: 'url' | 'form' | 'event';
  urlTemplate?: string;
  openIn?: 'same-tab' | 'new-tab';
  formId?: string;
  formIdField?: string;
  eventName?: string;
}

interface OrgChartConfig {
  // Data
  data?: unknown;

  // Field mappings
  idField?: string;
  nameField?: string;
  titleField?: string;
  descriptionField?: string;
  childrenField?: string;
  avatarField?: string;
  colorField?: string;

  // Layout
  layout?: 'vertical' | 'horizontal';
  cardStyle?: 'minimal' | 'detailed' | 'compact';

  // Appearance
  showDescription?: boolean;
  showAvatar?: boolean;
  connectorColor?: string;
  cardMinWidth?: number;
  cardMaxWidth?: number;

  // Interaction
  collapsible?: boolean;
  defaultExpanded?: boolean | number;

  // Node Actions
  nodeActions?: NodeAction[];
  nodeLinkUrlField?: string;
  nodeLinkLabelField?: string;
  nodeLinkOpenIn?: 'same-tab' | 'new-tab';
}

// ---------------------------------------------------------------------------
// Internal node type
// ---------------------------------------------------------------------------

interface OrgNode {
  _id: string;
  _name: string;
  _title: string;
  _description: string;
  _avatar: string;
  _color: string;
  _children: OrgNode[];
  _raw: Record<string, unknown>;
  _depth: number;
}

// ---------------------------------------------------------------------------
// Parsed config with all defaults resolved
// ---------------------------------------------------------------------------

interface ParsedConfig {
  idField: string;
  nameField: string;
  titleField: string;
  descriptionField: string;
  childrenField: string;
  avatarField: string;
  colorField: string;
  layout: 'vertical' | 'horizontal';
  cardStyle: 'minimal' | 'detailed' | 'compact';
  showDescription: boolean;
  showAvatar: boolean;
  connectorColor: string;
  cardMinWidth: number;
  cardMaxWidth: number;
  collapsible: boolean;
  defaultExpanded: boolean | number;
  nodeActions: NodeAction[];
  nodeLinkUrlField: string;
  nodeLinkLabelField: string;
  nodeLinkOpenIn: 'same-tab' | 'new-tab';
}

// ---------------------------------------------------------------------------
// Helper: parse config with safe defaults
// ---------------------------------------------------------------------------

function parseOrgConfig(config?: Record<string, unknown>): ParsedConfig {
  const c = (config ?? {}) as OrgChartConfig;
  return {
    idField: typeof c.idField === 'string' ? c.idField : 'id',
    nameField: typeof c.nameField === 'string' ? c.nameField : 'name',
    titleField: typeof c.titleField === 'string' ? c.titleField : 'title',
    descriptionField: typeof c.descriptionField === 'string' ? c.descriptionField : 'description',
    childrenField: typeof c.childrenField === 'string' ? c.childrenField : 'children',
    avatarField: typeof c.avatarField === 'string' ? c.avatarField : 'avatar',
    colorField: typeof c.colorField === 'string' ? c.colorField : 'color',
    layout: c.layout === 'horizontal' ? 'horizontal' : 'vertical',
    cardStyle: c.cardStyle === 'minimal' ? 'minimal' : c.cardStyle === 'compact' ? 'compact' : 'detailed',
    showDescription: c.showDescription !== false,
    showAvatar: c.showAvatar !== false,
    connectorColor: typeof c.connectorColor === 'string' ? c.connectorColor : '#94a3b8',
    cardMinWidth: typeof c.cardMinWidth === 'number' ? c.cardMinWidth : 180,
    cardMaxWidth: typeof c.cardMaxWidth === 'number' ? c.cardMaxWidth : 240,
    collapsible: c.collapsible !== false,
    defaultExpanded: c.defaultExpanded !== undefined ? c.defaultExpanded : true,
    nodeActions: Array.isArray(c.nodeActions) ? (c.nodeActions as NodeAction[]) : [],
    nodeLinkUrlField: typeof c.nodeLinkUrlField === 'string' ? c.nodeLinkUrlField : '',
    nodeLinkLabelField: typeof c.nodeLinkLabelField === 'string' ? c.nodeLinkLabelField : '',
    nodeLinkOpenIn: c.nodeLinkOpenIn === 'same-tab' ? 'same-tab' : 'new-tab',
  };
}

// ---------------------------------------------------------------------------
// Helper: build OrgNode tree
// ---------------------------------------------------------------------------

const MAX_DEPTH = 15;

function buildOrgNode(raw: unknown, cfg: ParsedConfig, depth = 0, siblingIndex = 0): OrgNode | null {
  if (depth > MAX_DEPTH) return null;
  if (raw === null || raw === undefined || typeof raw !== 'object') return null;

  const r = raw as Record<string, unknown>;

  const id = r[cfg.idField];
  // Use a deterministic positional fallback so React keys are stable across renders.
  const idStr = id !== undefined && id !== null ? String(id) : `__pos_${depth}_${siblingIndex}`;

  const name = r[cfg.nameField];
  const nameStr = name !== undefined && name !== null ? String(name) : '';

  const title = r[cfg.titleField];
  const titleStr = title !== undefined && title !== null ? String(title) : '';

  const description = r[cfg.descriptionField];
  const descStr = description !== undefined && description !== null ? String(description) : '';

  const avatar = r[cfg.avatarField];
  const avatarStr = typeof avatar === 'string' ? avatar : '';

  const color = r[cfg.colorField];
  const colorStr = typeof color === 'string' && color.startsWith('#') ? color : '#309B48';

  const rawChildren = r[cfg.childrenField];
  const childArray: OrgNode[] = [];
  if (Array.isArray(rawChildren)) {
    for (let i = 0; i < rawChildren.length; i++) {
      const built = buildOrgNode(rawChildren[i], cfg, depth + 1, i);
      if (built !== null) {
        childArray.push(built);
      }
    }
  }

  return {
    _id: idStr,
    _name: nameStr,
    _title: titleStr,
    _description: descStr,
    _avatar: avatarStr,
    _color: colorStr,
    _children: childArray,
    _raw: r,
    _depth: depth,
  };
}

// ---------------------------------------------------------------------------
// Helper: resolve {{fieldName}} template from node._raw
// ---------------------------------------------------------------------------

function resolveTemplate(template: string, node: OrgNode): string {
  return template.replace(/\{\{(\w+)\}\}/g, (_match, key: string) => {
    const val = node._raw[key];
    return val !== undefined && val !== null ? String(val) : '';
  });
}

// ---------------------------------------------------------------------------
// Helper: get up to 2 initials from a name
// ---------------------------------------------------------------------------

function getInitials(name: string): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

// ---------------------------------------------------------------------------
// Helper: handle a node action click
// ---------------------------------------------------------------------------

function handleNodeAction(action: NodeAction, node: OrgNode): void {
  try {
    if (action.actionType === 'url') {
      const template = action.urlTemplate ?? '';
      const url = resolveTemplate(template, node);
      if (!url) return;
      const target = action.openIn === 'same-tab' ? '_self' : '_blank';
      window.open(url, target, target === '_blank' ? 'noopener,noreferrer' : undefined);
    } else if (action.actionType === 'form') {
      const formId =
        action.formIdField
          ? String(node._raw[action.formIdField] ?? action.formId ?? '')
          : String(action.formId ?? '');
      const event = new CustomEvent('atlas-forms:open-form', {
        bubbles: true,
        detail: { formId, nodeId: node._id, nodeData: node._raw },
      });
      document.dispatchEvent(event);
    } else if (action.actionType === 'event') {
      const eventName = action.eventName ?? 'atlas-forms:node-action';
      const event = new CustomEvent(eventName, {
        bubbles: true,
        detail: { actionId: action.id, nodeId: node._id, nodeData: node._raw },
      });
      document.dispatchEvent(event);
    }
  } catch (err) {
    console.warn('[OrgChartControl] handleNodeAction error:', err);
  }
}

// ---------------------------------------------------------------------------
// Helper: compute initial collapsed set
// ---------------------------------------------------------------------------

function getInitialCollapsed(root: OrgNode, defaultExpanded: boolean | number): Set<string> {
  const collapsed = new Set<string>();

  function walk(node: OrgNode): void {
    if (node._children.length === 0) return;

    if (defaultExpanded === false) {
      // Collapse everything except root
      if (node._depth > 0) collapsed.add(node._id);
    } else if (defaultExpanded === true) {
      // Expand all — collapse nothing
    } else if (typeof defaultExpanded === 'number') {
      // Collapse nodes deeper than the threshold level
      if (node._depth >= defaultExpanded) {
        collapsed.add(node._id);
      }
    }

    for (const child of node._children) {
      walk(child);
    }
  }

  walk(root);
  return collapsed;
}

// ---------------------------------------------------------------------------
// OrgCard sub-component
// ---------------------------------------------------------------------------

interface OrgCardProps {
  node: OrgNode;
  isCollapsed: boolean;
  cfg: ParsedConfig;
  onToggle: (id: string) => void;
}

const OrgCard = memo<OrgCardProps>(({ node, isCollapsed, cfg, onToggle }) => {
  const hasChildren = node._children.length > 0;
  const accentColor = node._color || '#309B48';

  const handleToggle = useCallback(() => onToggle(node._id), [onToggle, node._id]);

  // Card dimensions
  const cardStyle: React.CSSProperties = {
    minWidth: cfg.cardMinWidth,
    maxWidth: cfg.cardMaxWidth,
    borderLeft: `4px solid ${accentColor}`,
    borderRadius: 6,
    backgroundColor: '#ffffff',
    boxShadow: '0 1px 4px rgba(0,0,0,0.10)',
    padding: cfg.cardStyle === 'minimal' ? '6px 10px' : cfg.cardStyle === 'compact' ? '8px 12px' : '10px 14px',
    position: 'relative',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  };

  // Avatar / initials
  const avatarSize = 36;
  const avatarStyle: React.CSSProperties = {
    width: avatarSize,
    height: avatarSize,
    borderRadius: '50%',
    objectFit: 'cover',
    flexShrink: 0,
  };

  const initialsStyle: React.CSSProperties = {
    width: avatarSize,
    height: avatarSize,
    borderRadius: '50%',
    backgroundColor: accentColor,
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 13,
    fontWeight: 700,
    flexShrink: 0,
    userSelect: 'none',
  };

  // Collapse toggle button (top-right, absolutely positioned)
  const toggleBtnStyle: React.CSSProperties = {
    position: 'absolute',
    top: 6,
    right: 6,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '2px 4px',
    fontSize: 10,
    color: '#6B7879',
    lineHeight: 1,
    borderRadius: 3,
  };

  // Row for avatar + text
  const headerRowStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
  };

  const textBlockStyle: React.CSSProperties = {
    flex: 1,
    minWidth: 0,
    paddingRight: hasChildren && cfg.collapsible ? 18 : 0,
  };

  const nameStyle: React.CSSProperties = {
    fontSize: 13,
    fontWeight: 700,
    color: '#101211',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    lineHeight: 1.3,
  };

  const titleStyle: React.CSSProperties = {
    fontSize: 11,
    color: '#6B7879',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    lineHeight: 1.3,
    marginTop: 1,
  };

  const descriptionStyle: React.CSSProperties = {
    fontSize: 11,
    color: '#6B7879',
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    lineHeight: 1.4,
    marginTop: 2,
  } as React.CSSProperties;

  // Action buttons row
  const actionRowStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4,
    marginTop: 4,
  };

  const actionBtnStyle: React.CSSProperties = {
    fontSize: 10,
    padding: '2px 8px',
    border: '1px solid #309B48',
    borderRadius: 3,
    background: 'none',
    color: '#309B48',
    cursor: 'pointer',
    lineHeight: 1.4,
    fontWeight: 500,
  };

  // Per-node link
  const nodeLinkUrl =
    cfg.nodeLinkUrlField && node._raw[cfg.nodeLinkUrlField]
      ? String(node._raw[cfg.nodeLinkUrlField])
      : '';
  const nodeLinkLabel =
    cfg.nodeLinkLabelField && node._raw[cfg.nodeLinkLabelField]
      ? String(node._raw[cfg.nodeLinkLabelField])
      : nodeLinkUrl;

  const showAvatar = cfg.showAvatar && cfg.cardStyle !== 'minimal';
  const showDescription = cfg.showDescription && cfg.cardStyle === 'detailed';

  return (
    <div style={cardStyle}>
      {/* Collapse toggle button */}
      {hasChildren && cfg.collapsible && (
        <button
          type="button"
          style={toggleBtnStyle}
          onClick={handleToggle}
          aria-label={isCollapsed ? `Expand ${node._name}` : `Collapse ${node._name}`}
        >
          {isCollapsed ? '▶' : '▼'}
        </button>
      )}

      {/* Header row: avatar + text */}
      <div style={headerRowStyle}>
        {showAvatar && (
          node._avatar
            ? <img src={node._avatar} alt={node._name} style={avatarStyle} />
            : <div style={initialsStyle} aria-hidden="true">{getInitials(node._name)}</div>
        )}

        <div style={textBlockStyle}>
          {node._name && <div style={nameStyle} title={node._name}>{node._name}</div>}
          {node._title && <div style={titleStyle} title={node._title}>{node._title}</div>}
          {showDescription && node._description && (
            <div style={descriptionStyle}>{node._description}</div>
          )}
        </div>
      </div>

      {/* Per-node link */}
      {nodeLinkUrl && (
        <div>
          <a
            href={nodeLinkUrl}
            target={cfg.nodeLinkOpenIn === 'same-tab' ? '_self' : '_blank'}
            rel={cfg.nodeLinkOpenIn !== 'same-tab' ? 'noopener noreferrer' : undefined}
            style={{ fontSize: 11, color: '#309B48', textDecoration: 'underline' }}
          >
            {nodeLinkLabel}
          </a>
        </div>
      )}

      {/* Action buttons */}
      {cfg.nodeActions.length > 0 && (
        <div style={actionRowStyle}>
          {cfg.nodeActions.map((action) => (
            <button
              key={action.id}
              type="button"
              style={actionBtnStyle}
              onClick={() => handleNodeAction(action, node)}
            >
              {action.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
});

OrgCard.displayName = 'OrgCard';

// ---------------------------------------------------------------------------
// OrgChartNode — recursive component
// ---------------------------------------------------------------------------

interface OrgChartNodeProps {
  node: OrgNode;
  collapsed: Set<string>;
  cfg: ParsedConfig;
  onToggle: (id: string) => void;
}

const OrgChartNode = memo<OrgChartNodeProps>(({ node, collapsed, cfg, onToggle }) => {
  const isCollapsed = collapsed.has(node._id);
  const hasVisibleChildren = node._children.length > 0 && !isCollapsed;
  const connectorColor = cfg.connectorColor;

  if (cfg.layout === 'horizontal') {
    // Horizontal layout: children appear to the right
    return (
      <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start' }}>
        {/* Card column */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <OrgCard node={node} isCollapsed={isCollapsed} cfg={cfg} onToggle={onToggle} />
        </div>

        {/* Connector + children */}
        {hasVisibleChildren && (
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start' }}>
            {/* Horizontal connector from card to vertical bar */}
            <div style={{ width: 20, height: 2, background: connectorColor, alignSelf: 'center' }} />

            {/* Vertical bar + children rows */}
            <div style={{ display: 'flex', flexDirection: 'column', position: 'relative' }}>
              {/* Vertical bar */}
              {node._children.length > 1 && (
                <div style={{
                  position: 'absolute',
                  left: 0,
                  top: `calc(100% / ${node._children.length * 2})`,
                  bottom: `calc(100% / ${node._children.length * 2})`,
                  width: 2,
                  background: connectorColor,
                }} />
              )}

              {node._children.map((child) => (
                <div key={child._id} style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', padding: '8px 0' }}>
                  {/* Short horizontal drop to child */}
                  <div style={{ width: 20, height: 2, background: connectorColor }} />
                  <OrgChartNode node={child} collapsed={collapsed} cfg={cfg} onToggle={onToggle} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Vertical (top-down) layout — default
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      {/* Card */}
      <OrgCard node={node} isCollapsed={isCollapsed} cfg={cfg} onToggle={onToggle} />

      {/* Subtree */}
      {hasVisibleChildren && (
        <>
          {/* Vertical drop from card bottom */}
          <div style={{ width: 2, height: 20, background: connectorColor }} />

          {/* Children row */}
          <div style={{ display: 'flex', flexDirection: 'row', position: 'relative', alignItems: 'flex-start' }}>
            {/* Horizontal connector bar spanning children (only if > 1 child) */}
            {node._children.length > 1 && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: `calc(100% / ${node._children.length * 2})`,
                right: `calc(100% / ${node._children.length * 2})`,
                height: 2,
                background: connectorColor,
              }} />
            )}

            {node._children.map((child) => (
              <div
                key={child._id}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, padding: '0 8px' }}
              >
                {/* Vertical drop to child */}
                <div style={{ width: 2, height: 20, background: connectorColor }} />
                <OrgChartNode node={child} collapsed={collapsed} cfg={cfg} onToggle={onToggle} />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
});

OrgChartNode.displayName = 'OrgChartNode';

// ---------------------------------------------------------------------------
// Constant styles — defined outside component to avoid recreation per render
// ---------------------------------------------------------------------------

const OUTER_STYLE: React.CSSProperties = {
  overflowX: 'auto',
  overflowY: 'auto',
  padding: 16,
  fontFamily: 'inherit',
};

const LABEL_STYLE: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  color: '#101211',
  marginBottom: 12,
  display: 'block',
};

// ---------------------------------------------------------------------------
// OrgChartControl — main exported component
// ---------------------------------------------------------------------------

export const OrgChartControl = memo((props: ControlRendererProps) => {
  const { control, value, mode } = props;

  // 1. Parse config
  const cfg = useMemo(
    () => parseOrgConfig(control.config as Record<string, unknown> | undefined),
    [control.config],
  );

  // 2. Resolve data: runtime value > config.data > control.defaultValue
  const rawData = useMemo(() => {
    if (value !== undefined && value !== null) return value;
    const configData = (control.config as Record<string, unknown> | undefined)?.data;
    if (configData !== undefined) return configData;
    return control.defaultValue;
  }, [value, control.config, control.defaultValue]);

  // 3. Build OrgNode tree
  const rootNode = useMemo(() => buildOrgNode(rawData, cfg, 0), [rawData, cfg]);

  // 4. Collapsed state: Set<nodeId>
  // Initialised once; reset via useEffect when the underlying data reference changes.
  const [collapsed, setCollapsed] = useState<Set<string>>(() =>
    rootNode ? getInitialCollapsed(rootNode, cfg.defaultExpanded) : new Set<string>(),
  );

  // Reset collapsed state when source data changes or when the defaultExpanded setting changes.
  const prevRawDataRef = useRef(rawData);
  const prevDefaultExpandedRef = useRef(cfg.defaultExpanded);
  useEffect(() => {
    const dataChanged = prevRawDataRef.current !== rawData;
    const settingChanged = prevDefaultExpandedRef.current !== cfg.defaultExpanded;
    if (dataChanged || settingChanged) {
      prevRawDataRef.current = rawData;
      prevDefaultExpandedRef.current = cfg.defaultExpanded;
      setCollapsed(rootNode ? getInitialCollapsed(rootNode, cfg.defaultExpanded) : new Set<string>());
    }
  }, [rawData, rootNode, cfg.defaultExpanded]);

  const handleToggle = useCallback((id: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // 5. Empty / design-mode state
  if (!rootNode) {
    const emptyMessage =
      mode === 'design'
        ? 'Org Chart — connect data or set static data in properties'
        : 'No data to display.';

    return (
      <div
        className="af-org-chart"
        style={{
          ...OUTER_STYLE,
          border: mode === 'design' ? '2px dashed #94a3b8' : undefined,
          borderRadius: 6,
          backgroundColor: mode === 'design' ? '#f8fafc' : undefined,
          color: '#6B7879',
          fontSize: 13,
          textAlign: 'center',
          padding: 24,
        }}
        data-control-id={control.id}
      >
        {control.label && <span style={LABEL_STYLE}>{control.label}</span>}
        <div>{emptyMessage}</div>
      </div>
    );
  }

  return (
    <div
      className="af-org-chart"
      style={OUTER_STYLE}
      data-control-id={control.id}
    >
      {control.label && <span style={LABEL_STYLE}>{control.label}</span>}
      <OrgChartNode
        node={rootNode}
        collapsed={collapsed}
        cfg={cfg}
        onToggle={handleToggle}
      />
    </div>
  );
});

OrgChartControl.displayName = 'OrgChartControl';

export default OrgChartControl;
